import { create } from 'zustand';
import { MiniappError, messageOf } from '@/api/miniapp/client';
import { miniappApi } from '@/api/miniapp/miniappApi';
import { useBackendCourseStore } from './backendCourseStore';
import type { AiChatAttachment, AiChatMessage, AiChatOperation } from '@/api/miniapp/types';
import { applyUploads, markSent, placeOf, releaseUploads, type LocalUpload } from './aiChat/uploads';
import { faultOf, type AiChatFault } from './aiChat/faults';
import {
  hasNewAssistantReply,
  hasNewReadyOperation,
  hasReadyPreview,
  preparePreview,
  type PreviewContext,
  type PreviewOutcome,
} from './aiChat/runs';

export type { AiChatFault } from './aiChat/faults';

/**
 * AI-чат по заданию на реальном бэкенде.
 *
 * Чат живёт на сервере, а не в приложении: у пользователя есть ровно один
 * активный чат, и закрытие мини-приложения его
 * не завершает. Поэтому при открытии урока всегда вызывается `start-chat-ai`:
 * ответ с `resumed: true` означает возврат в уже идущий диалог, а не ошибку.
 * Завершают чат только `final-answer` и `cancel-chat-ai`.
 *
 * Ответ агента на отправленный файл приходит асинхронно через callback, поэтому
 * единственный достоверный источник переписки — `history-chat`, а не локально
 * накопленный список сообщений.
 */

interface AiChatState {
  sessionId: string | null;
  /** Диалог продолжен после закрытия приложения, а не начат заново. */
  resumed: boolean;
  /** Чат успешно открыт: сбой отправки уже не должен подменять экран ошибкой. */
  opened: boolean;
  messages: AiChatMessage[];
  /**
   * Переписка без локальных вложений: история сервера плюс свои сообщения,
   * которые он ещё не показал. От неё считаются места вложений — считать от
   * готовой ленты нельзя, номера сдвигались бы от собственных добавлений.
   */
  history: AiChatMessage[];
  /** Отправленные вложения этого сеанса: в истории сервера их нет. */
  uploads: LocalUpload[];
  /** Агент готовит ответ: он приходит вебхуком, а не в ответе на отправку. */
  awaitingReply: boolean;
  /** Канал ответов подключён: пока он жив, историю опрашивать незачем. */
  socketAlive: boolean;
  /**
   * Отметка последнего готового материала. Меняется и от сокета, и от истории,
   * поэтому ожидание итога не зависит от того, каким путём он пришёл.
   */
  artifactSeenAt: number;
  /** Итоги задач: предпросмотр сдачи и финальный ответ с готовыми файлами. */
  operations: AiChatOperation[];
  starting: boolean;
  loadingHistory: boolean;
  sending: boolean;
  uploading: boolean;
  /** Чат завершён отправкой или отменой: ввод больше не принимается. */
  finished: boolean;
  /** Ассистент выполняет поставленную задачу: предпросмотр или сдачу. */
  runPending: boolean;
  fault: AiChatFault | null;
  error: string | null;

  /** Открыть чат по заданию: создаёт новый либо возвращает активный. */
  open(assignmentId: number | string, variantId?: number | string): Promise<boolean>;
  /** Сообщение из канала ответов: агент прислал текст, возможно с файлом. */
  applySocketMessage(message: { text: string; attachment: unknown }): void;
  setSocketAlive(alive: boolean): void;
  /** Завершить зависший диалог и открыть новый по тому же заданию. */
  restart(assignmentId: number | string, variantId?: number | string): Promise<boolean>;
  loadHistory(): Promise<void>;
  send(text: string): Promise<boolean>;
  attach(file: File): Promise<boolean>;
  /** Голосовой ответ: сервер принимает его так же, как файл. */
  sendVoice(audio: Blob, durationMs: number): Promise<boolean>;
  /** Собирает итог и сообщает, чем закончилось: файл, вопросы агента или ожидание. */
  preview(): Promise<PreviewOutcome | 'failed'>;
  submit(): Promise<boolean>;
  cancel(): Promise<boolean>;
  /**
   * Итог собран и принят ассистентом.
   *
   * До этого сдавать нечего: бэкенд принимает ответ только после готового
   * итога, а человек ещё не видел, что именно отправляет.
   */
  previewReady(): boolean;
  downloadAttachment(attachment: AiChatAttachment): Promise<Blob | null>;
  reset(): void;
}

const initial = {
  sessionId: null,
  resumed: false,
  opened: false,
  messages: [] as AiChatMessage[],
  history: [] as AiChatMessage[],
  uploads: [] as LocalUpload[],
  awaitingReply: false,
  socketAlive: false,
  artifactSeenAt: 0,
  operations: [] as AiChatOperation[],
  starting: false,
  loadingHistory: false,
  sending: false,
  uploading: false,
  finished: false,
  runPending: false,
  fault: null as AiChatFault | null,
  error: null as string | null,
};

type SetState = (
  partial: Partial<AiChatState> | ((state: AiChatState) => Partial<AiChatState>)
) => void;

/**
 * Отправка вложения: файла или записи голоса.
 *
 * Пути отличаются только ручкой и текстом отказа: вложение сразу видно в
 * переписке, при неудаче исчезает из неё, при успехе остаётся ждать ответа
 * агента. Двумя копиями это значило бы чинить каждую ошибку дважды.
 */
function sendUpload(
  set: SetState,
  get: () => AiChatState,
  plan: {
    upload: LocalUpload;
    send(): Promise<unknown>;
    /** Отказ по содержимому, а не по связи: человеку нужен другой совет. */
    rejected(error: unknown): boolean;
    rejectedMessage: string;
    failedMessage: string;
    /** Освободить ресурсы вложения, которое не ушло. */
    onDiscard?(): void;
  }
): Promise<boolean> {
  const { upload } = plan;

  set((state) => {
    const uploads = [...state.uploads, upload];
    return {
      uploading: true,
      error: null,
      fault: null,
      uploads,
      messages: applyUploads(state.history, uploads),
    };
  });

  return plan
    .send()
    .then(async () => {
      // Ответ агента приходит асинхронно, поэтому берём историю с сервера
      set((state) => {
        const uploads = markSent(state.uploads, upload);
        return {
          uploading: false,
          awaitingReply: true,
          uploads,
          messages: applyUploads(state.history, uploads),
        };
      });
      await get().loadHistory();
      return true;
    })
    .catch((error: unknown) => {
      const rejected = plan.rejected(error);
      plan.onDiscard?.();
      set((state) => {
        // Неотправленное вложение убирается: иначе переписка разойдётся с сервером
        const uploads = state.uploads.filter((item) => item !== upload);
        return {
          uploading: false,
          awaitingReply: false,
          uploads,
          messages: applyUploads(state.history, uploads),
          fault: rejected ? null : faultOf(error),
          error: rejected ? plan.rejectedMessage : messageOf(error, plan.failedMessage),
        };
      });
      return false;
    });
}

/** Доступ к состоянию для ожидания задачи: ждать умеет модуль, а не стор. */
function previewContext(get: () => AiChatState): PreviewContext {
  return {
    operations: () => get().operations,
    messages: () => get().history,
    artifactSeenAt: () => get().artifactSeenAt,
    socketAlive: () => get().socketAlive,
    reloadHistory: () => get().loadHistory(),
  };
}

export const useAiChatStore = create<AiChatState>((set, get) => ({
  ...initial,

  async open(assignmentId, variantId) {
    // Прошлые записи больше не нужны: состояние сбрасывается целиком
    releaseUploads(get().uploads);
    set({ ...initial, starting: true });
    try {
      const response = await miniappApi.startChat({
        assignment_id: assignmentId,
        variant_id: variantId,
      });
      set({
        sessionId: response.session_id ?? null,
        resumed: response.resumed === true,
        opened: true,
        starting: false,
      });
      // Продолженный чат уже содержит переписку; у нового история пуста, но
      // запрос всё равно дешевле, чем расхождение с сервером
      await get().loadHistory();
      return true;
    } catch (error) {
      set({ starting: false, fault: faultOf(error), error: messageOf(error, 'Не удалось открыть чат') });
      return false;
    }
  },

  async restart(assignmentId, variantId) {
    set({ starting: true, error: null, fault: null });
    try {
      // Завершение освобождает чат: пока прошлая задача числится активной,
      // сервер отклоняет новые запросы
      await miniappApi.cancelChat();
    } catch {
      /* чат мог завершиться сам: открываем новый в любом случае */
    }
    return get().open(assignmentId, variantId);
  },

  setSocketAlive(alive) {
    set({ socketAlive: alive });
  },

  applySocketMessage(message) {
    const text = String(message.text ?? '').trim();

    set((state) => {
      if (!text) return { awaitingReply: false };

      // Один и тот же ответ может прийти дважды: канал переподключился, а
      // сервер повторил доставку. Дословный повтор подряд у агента не
      // встречается, поэтому такой дубль в ленту не попадает
      const last = state.history[state.history.length - 1];
      if (last?.role === 'assistant' && last.content === text) return { awaitingReply: false };

      const history = [...state.history, { role: 'assistant' as const, content: text }];
      // Ответ агента получен: ожидание снимается сразу, без запроса истории
      return { awaitingReply: false, history, messages: applyUploads(history, state.uploads) };
    });

    // Файл приходит отдельным полем, но его адрес и идентификатор задаёт
    // история: она и остаётся источником истины по вложениям
    if (message.attachment) {
      set({ artifactSeenAt: Date.now() });
      void get().loadHistory();
    }
  },

  async loadHistory() {
    set({ loadingHistory: true });
    try {
      const response = await miniappApi.getChatHistory();
      const messages = response.data?.messages ?? [];

      set((state) => {
        // Неподтверждённые сообщения дописываются в конец: сервер ещё не
        // показал их, но на экране они уже есть и пропадать не должны.
        // Вложения сюда не попадают — их место определяет applyUploads
        const history = [
          ...messages,
          ...state.history.filter(
            (item) =>
              item.pending &&
              !messages.some((message) => message.role === 'user' && message.content === item.content)
          ),
        ];

        return {
          history,
          messages: applyUploads(history, state.uploads),
          operations: response.data?.operations ?? [],
          loadingHistory: false,
          // Ответ агента приходит вебхуком: ожидание снимается его появлением
          awaitingReply: state.awaitingReply && !hasNewAssistantReply(state.history, messages),
          artifactSeenAt: hasNewReadyOperation(state.operations, response.data?.operations ?? [])
            ? Date.now()
            : state.artifactSeenAt,
        };
      });
    } catch (error) {
      set({
        loadingHistory: false,
        fault: faultOf(error),
        error: messageOf(error, 'Не удалось загрузить переписку'),
      });
    }
  },

  async send(text) {
    const value = text.trim();
    if (!value || get().sending || get().finished) return false;

    // Своё сообщение показывается сразу: ждать ответа агента, чтобы увидеть
    // собственный текст, — заметная задержка на телефоне
    set((state) => {
      const history = [...state.history, { role: 'user' as const, content: value, pending: true }];
      return {
        sending: true,
        error: null,
        fault: null,
        history,
        messages: applyUploads(history, state.uploads),
      };
    });

    try {
      // Отправка возвращает только статус: ответ агента придёт вебхуком и
      // прилетит по сокету, поэтому ожидание снимает не этот запрос
      await miniappApi.sendChatMessage(value);
      set({ sending: false, awaitingReply: true });
      await get().loadHistory();
      return true;
    } catch (error) {
      set((state) => {
        // Неотправленное сообщение убирается, иначе история разойдётся с сервером
        const history = state.history.filter((item) => !(item.pending && item.content === value));
        // Открытый чат сбоем отправки не ломается: показывать «диалог не
        // открылся» неверно — текст остался в поле, и его можно отправить снова
        const fault = faultOf(error);
        return {
          sending: false,
          awaitingReply: false,
          fault: fault === 'unknown' ? null : fault,
          error:
            fault === 'unknown'
              ? 'Сообщение не отправлено. Нажмите отправку ещё раз'
              : messageOf(error, 'Сообщение не отправлено'),
          history,
          messages: applyUploads(history, state.uploads),
        };
      });
      return false;
    }
  },

  attach(file) {
    return sendUpload(set, get, {
      // Отправленный файл сразу виден в переписке: истории сервера он не
      // достаётся, и без отметки отправка выглядела бы как бездействие
      upload: { kind: 'file', filename: file.name, sending: true, ...placeOf(get().history) },
      send: () => miniappApi.uploadAiFile(file),
      // Непонятный сервису файл отклоняется с 422: общая формулировка про сбой
      // отправила бы человека проверять связь вместо смены файла
      rejected: (error) => error instanceof MiniappError && error.status === 422,
      rejectedMessage:
        'Ассистент не смог прочитать файл. Попробуйте другой — например, PDF или обычный текст.',
      failedMessage: 'Не удалось прикрепить файл',
    });
  },

  sendVoice(audio, durationMs) {
    // Запись показывается сразу и остаётся в переписке: сервер вернёт только
    // расшифровку, а услышать сказанное человек должен мочь и после отправки
    const url = URL.createObjectURL(audio);

    return sendUpload(set, get, {
      upload: { kind: 'voice', url, durationMs, sending: true, ...placeOf(get().history) },
      send: () => miniappApi.uploadAiAudio(audio),
      // Сервис расшифровки отвечает 500 с кодом причины внутри тела, а не 422:
      // «речь не распознана» — не сбой связи, и человеку нужен другой совет
      rejected: (error) =>
        error instanceof MiniappError &&
        (error.status === 422 || error.type === 'voice_transcription_empty'),
      rejectedMessage:
        'В записи не распознана речь. Говорите ближе к микрофону и попробуйте ещё раз.',
      failedMessage: 'Не удалось отправить запись',
      onDiscard: () => URL.revokeObjectURL(url),
    });
  },

  async preview() {
    set({ runPending: true, error: null, fault: null });
    try {
      const outcome = await preparePreview(previewContext(get));
      set({
        runPending: false,
        // Ответ агента вместо итога — не ошибка: он просит уточнений, и они
        // уже видны в переписке
        error: outcome === 'timeout' ? 'Ассистент ещё собирает итог. Файл появится в переписке.' : null,
      });
      return outcome;
    } catch (error) {
      set({
        runPending: false,
        fault: faultOf(error),
        error: messageOf(error, 'Не удалось собрать итог'),
      });
      return 'failed';
    }
  },

  previewReady() {
    // Признак готовности приходит двумя путями: операцией в истории и
    // сообщением по каналу ответов. Достаточно любого
    return hasReadyPreview(get().operations) || get().artifactSeenAt > 0;
  },

  async submit() {
    // Сдача без готового итога не проходит на сервере и не имеет смысла:
    // человек должен увидеть, что именно отправляет
    if (!get().previewReady()) {
      set({ fault: 'needs_preview', error: null });
      return false;
    }

    set({ runPending: true, error: null, fault: null });
    try {
      // Сдача операции в истории не создаёт: бэкенд отмечает задание принятым
      // и отвечает благодарностью, поэтому ждать здесь нечего
      await miniappApi.finalAnswer();

      // Список уроков перечитывается при следующем открытии модуля
      useBackendCourseStore.getState().invalidate();
      set({ finished: true, runPending: false, sessionId: null, error: null, fault: null });
      return true;
    } catch (error) {
      set({ runPending: false, fault: faultOf(error), error: messageOf(error, 'Не удалось отправить ответ') });
      return false;
    }
  },

  async cancel() {
    try {
      await miniappApi.cancelChat();
      set({ ...initial, finished: true });
      return true;
    } catch (error) {
      set({ fault: faultOf(error), error: messageOf(error, 'Не удалось завершить чат') });
      return false;
    }
  },

  async downloadAttachment(attachment) {
    try {
      return await miniappApi.downloadAiFile(attachment.url);
    } catch (error) {
      const fault = faultOf(error);
      set({ fault, error: messageOf(error, 'Вложение недоступно') });
      // Пропавший файл означает устаревшую историю: перечитываем её
      if (fault === 'file_missing') await get().loadHistory();
      return null;
    }
  },

  reset() {
    releaseUploads(get().uploads);
    set({ ...initial });
  },
}));
